import React, {useEffect, useMemo, useState } from "react";
import { normalizeBillingAccount } from "../../utils/helpers";

import CustomSelect from "../ui/CustomSelect";
const fieldBaseClass =
  "w-full rounded-md border border-gray-300 px-2.5 py-2 text-sm focus:border-[var(--accent,#a51c1c)] focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/25";

const activeFieldClass = `${fieldBaseClass} bg-white text-gray-800 placeholder:text-gray-400`;
const inactiveFieldClass = `${fieldBaseClass} bg-gray-50 text-gray-400 placeholder:text-gray-300`;

// Small, self-contained confirm-delete modal styled to match the app's
// existing "Confirm Delete" dialog (white card, bold header, gray warning
// box, Cancel + red destructive action).
const ConfirmDeleteModal = ({ label, onCancel, onConfirm }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
    role="dialog"
    aria-modal="true"
    aria-labelledby="confirm-delete-title"
  >
    <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
      <h3 id="confirm-delete-title" className="text-base font-semibold text-gray-900">
        Confirm Delete
      </h3>

      <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
        This will remove <span className="font-semibold">{label}</span> from your billing
        account list.
      </div>

      <p className="mt-3 text-xs text-gray-500">
        This action cannot be undone.
      </p>

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
        >
          Delete
        </button>
      </div>
    </div>
  </div>
);

const BillingAccountsSettings = ({
  providers,
  accounts,
  onProvidersChange,
  onAccountsChange,
  loading,
}) => {
  const [providerDraft, setProviderDraft] = useState("");
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState(null);
  const [newAccountId, setNewAccountId] = useState(null);

  useEffect(() => {
    if (!newAccountId) return;
  
    const newRow = document.querySelector(
      `[data-account-id="${newAccountId}"]`
    );
  
    if (newRow) {
      newRow.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  
    setNewAccountId(null);
  }, [accounts, newAccountId]);

  const providerNames = useMemo(
    () => providers.map((item) => String(item?.provider ?? item ?? "").trim()).filter(Boolean),
    [providers],
  );

  const duplicateKeys = useMemo(() => {
    const counts = new Map();
    accounts.forEach((account) => {
      const provider = String(account?.provider ?? "").trim().toLowerCase();
      const billingAccount = normalizeBillingAccount(account?.billing_account).toLowerCase();
      if (!provider || !billingAccount) return;
      const key = `${provider}\u0000${billingAccount}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return new Set(
      [...counts.entries()].filter(([, count]) => count > 1).map(([key]) => key),
    );
  }, [accounts]);

  const duplicateMessage = useMemo(() => {
    const seen = new Set();
    for (const account of accounts) {
      const provider = String(account?.provider ?? "").trim();
      const billingAccount = normalizeBillingAccount(account?.billing_account);
      if (!provider || !billingAccount) continue;
      const key = `${provider.toLowerCase()}\u0000${billingAccount.toLowerCase()}`;
      if (seen.has(key)) {
        return `Duplicate billing account ${billingAccount} for ${provider}`;
      }
      seen.add(key);
    }
    return null;
  }, [accounts]);

  const addProvider = () => {
    const provider = providerDraft.trim();
    if (
      !provider ||
      providerNames.some((value) => value.toLowerCase() === provider.toLowerCase())
    ) {
      return;
    }
    onProvidersChange([...providers, { provider }]);
    setProviderDraft("");
  };

  const removeProvider = (provider) => {
    const linkedCount = accounts.filter((account) => account.provider === provider).length;
    if (
      linkedCount > 0 &&
      !window.confirm(
        `Remove ${provider} and its ${linkedCount} billing account${
          linkedCount === 1 ? "" : "s"
        }?`,
      )
    ) {
      return;
    }
    onProvidersChange(
      providers.filter(
        (item) => String(item?.provider ?? item ?? "").trim() !== provider,
      ),
    );
    onAccountsChange(accounts.filter((account) => account.provider !== provider));
  };
  
  const addAccount = () => {
    const newAccount = {
      client_id: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      provider: "",
      billing_account: "",
      display_name: "",
      is_active: true,
    };
  
    onAccountsChange([...accounts, newAccount]);
    setNewAccountId(newAccount.client_id);
  };

  const updateAccount = (index, patch) => {
    onAccountsChange(
      accounts.map((account, accountIndex) =>
        accountIndex === index ? { ...account, ...patch } : account,
      ),
    );
  };

  const requestRemoveAccount = (index) => {
    setPendingRemoveIndex(index);
  };

  const cancelRemoveAccount = () => {
    setPendingRemoveIndex(null);
  };

  const confirmRemoveAccount = () => {
    if (pendingRemoveIndex === null) return;
    onAccountsChange(
      accounts.filter((_, accountIndex) => accountIndex !== pendingRemoveIndex),
    );
    setPendingRemoveIndex(null);
  };

  const pendingAccount =
    pendingRemoveIndex !== null ? accounts[pendingRemoveIndex] : null;
  const pendingLabel = pendingAccount
    ? [pendingAccount.provider, pendingAccount.billing_account]
        .filter(Boolean)
        .join(" — ") || "this billing account"
    : "";

  return (
    <div className="space-y-5 pb-2">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Billing Accounts</h3>
        <p className="mt-1 max-w-3xl text-sm text-gray-500">
          These are the only providers and active accounts available for invoice uploads
          and budget entry.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
        <div className="flex items-baseline gap-2">
          <h4 className="text-sm font-semibold text-gray-900">Providers</h4>
          <span className="text-xs text-gray-500">{providerNames.length} providers</span>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Add each carrier once, then select it on the billing account rows below.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {providerNames.map((provider) => (
            <span
              key={provider}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700"
            >
              {provider}
              <button
                type="button"
                onClick={() => removeProvider(provider)}
                disabled={loading}
                className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                aria-label={`Remove ${provider}`}
              >
                ×
              </button>
            </span>
          ))}
          {providerNames.length === 0 && (
            <span className="text-xs text-gray-500">No providers configured.</span>
          )}
        </div>
        <div className="mt-3 flex max-w-lg gap-2">
          <input
            type="text"
            value={providerDraft}
            onChange={(event) => setProviderDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addProvider();
              }
            }}
            disabled={loading}
            placeholder="Add a provider"
            className={activeFieldClass}
            maxLength={200}
          />
          <button
            type="button"
            onClick={addProvider}
            disabled={!providerDraft.trim() || loading}
            className="shrink-0 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Add provider
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          <span className="font-semibold text-gray-900">{accounts.length}</span>{" "}
          billing accounts · {accounts.filter((account) => account.is_active).length} active
        </p>
        <button
          type="button"
          onClick={addAccount}
          disabled={loading || providerNames.length === 0}
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          + Add billing account
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-[820px] w-full text-left">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2.5 font-medium">Provider</th>
              <th className="px-3 py-2.5 font-medium">Account number</th>
              <th className="px-3 py-2.5 font-medium">Display name</th>
              <th className="px-3 py-2.5 font-medium">Shown as</th>
              <th className="px-3 py-2.5 font-medium">Status</th>
              <th className="px-3 py-2.5 font-medium" aria-label="Actions" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {accounts.map((account, index) => {
              const isActive = account.is_active !== false;
              const shownAs = account.display_name?.trim()
                ? `${account.billing_account || ""} - ${account.display_name.trim()}`
                : account.billing_account || "—";
              const accountKey = `${String(account?.provider ?? "")
                .trim()
                .toLowerCase()}\u0000${normalizeBillingAccount(
                account?.billing_account,
              ).toLowerCase()}`;
              const isDuplicate = duplicateKeys.has(accountKey);
              const rowFieldClass = `${isActive ? activeFieldClass : inactiveFieldClass}${
                isDuplicate ? " border-red-500 focus:border-red-500 focus:ring-red-200" : ""
              }`;
              return (
                <tr key={account.id || account.client_id || index}
                data-account-id={account.client_id}
                >
                  <td className="px-3 py-2">
                    <CustomSelect
                      value={account.provider}
                      onChange={(event) =>
                        updateAccount(index, { provider: event.target.value })
                      }
                      disabled={loading}
                      className={rowFieldClass}
                    >
                      <option value="">Select provider</option>
                      {providerNames.map((provider) => (
                        <option key={provider} value={provider}>
                          {provider}
                        </option>
                      ))}
                    </CustomSelect>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={account.billing_account || ""}
                      onChange={(event) =>
                        updateAccount(index, { billing_account: event.target.value })
                      }
                      disabled={loading}
                      className={rowFieldClass}
                      maxLength={255}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={account.display_name || ""}
                      onChange={(event) =>
                        updateAccount(index, { display_name: event.target.value })
                      }
                      disabled={loading}
                      placeholder="Optional"
                      className={rowFieldClass}
                      maxLength={255}
                    />
                  </td>
                  <td
                    className={`px-3 py-2 text-xs ${
                      isActive ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    {shownAs}
                  </td>
                  <td className="px-3 py-2">
                    <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-gray-600">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={isActive}
                        onClick={() =>
                          updateAccount(index, { is_active: !isActive })
                        }
                        disabled={loading}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500/40 disabled:opacity-50 ${
                          isActive ? "bg-emerald-500" : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            isActive ? "translate-x-4" : "translate-x-0.5"
                          }`}
                        />
                      </button>
                      <span className={isActive ? "text-gray-700" : "text-gray-400"}>
                        {isActive ? "Active" : "Closed"}
                      </span>
                    </label>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => requestRemoveAccount(index)}
                      disabled={loading}
                      className="text-xs font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              );
            })}
            {accounts.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                  No billing accounts configured.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {duplicateMessage && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {duplicateMessage}
        </div>
      )}

      {pendingRemoveIndex !== null && (
        <ConfirmDeleteModal
          label={pendingLabel}
          onCancel={cancelRemoveAccount}
          onConfirm={confirmRemoveAccount}
        />
      )}
    </div>
  );
};

export default BillingAccountsSettings;
